import { env } from 'next-runtime-env'
import { PublicEnvScript } from 'next-runtime-env'
import type { ZodSafeParseResult } from 'zod'
import type { AppConfig, AppConfigDynamic, AppConfigStatic } from './entities'
import { AppConfigSchema } from './entities'

export const InjectConfigScript = PublicEnvScript

declare global {
  interface Window {
    __appVersion: string
  }
}

let appConfig: AppConfig

export const getConfig = (): AppConfig => {
  if (appConfig) {
    return appConfig
  }

  const staticConfig: AppConfigStatic = {
    isBrowser: Boolean(process.browser),
    name: 'customer-web-react',
    project: 'customer-web',
    appType: 'customer',
    platform: 'web',
    isDevelopment: env('NEXT_PUBLIC_ENV') === 'development',
  }
  const parseResult: ZodSafeParseResult<AppConfigDynamic> = AppConfigSchema.safeParse({
    requestTTL: env('NEXT_PUBLIC_REQUEST_TTL'),
    apiVersion: env('NEXT_PUBLIC_API_VERSION'),
    developmentState: env('NEXT_PUBLIC_DEVELOPMENT_STATE'),
    env: env('NEXT_PUBLIC_ENV'),
    imageServiceCdn: env('NEXT_PUBLIC_IMAGE_SERVICE_CDN'),
    apiUrl: env('NEXT_PUBLIC_API_URL'),
    devVueAppUrl: env('NEXT_PUBLIC_DEV_VUE_APP_URL'),
    rumApplicationId: env('NEXT_PUBLIC_RUM_APPLICATION_ID'),
    rumClientToken: env('NEXT_PUBLIC_RUM_CLIENT_TOKEN'),
    version: env('NEXT_PUBLIC_APP_VERSION'),
    userCentricsScriptId: env('NEXT_PUBLIC_USER_CENTRICS_SCRIPT_ID'),
    userCentricsGoogleConsentServiceId: env('NEXT_PUBLIC_USER_CENTRICS_GOOGLE_CONSENT_SERVICE_ID'),
    googleClientId: env('NEXT_PUBLIC_GOOGLE_CLIENT_ID'),
    googleMapsKey: env('NEXT_PUBLIC_GOOGLE_MAPS_API_KEY'),
    mParticleId: env('NEXT_PUBLIC_MPARTICLE_ID'),
    facebookAppId: env('NEXT_PUBLIC_FACEBOOK_APP_ID'),
    brazeId: env('NEXT_PUBLIC_BRAZE_ID'),
    kustomerApiKey: env('NEXT_PUBLIC_KUSTOMER_API_KEY'),
    reCaptchaKey: env('NEXT_PUBLIC_RECAPTCHA_API_KEY'),
    googleTagManagerId: env('NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID'),
    paymentHost: env('NEXT_PUBLIC_PAYMENT_HOST'),
    appStoreUrl: env('NEXT_PUBLIC_APP_STORE_URL'),
    playMarketUrl: env('NEXT_PUBLIC_PLAY_MARKET_URL'),
    applePayMerchantId: env('NEXT_PUBLIC_APPLE_PAY_MERCHANT_ID'),
    googleMerchantId: env('NEXT_PUBLIC_GOOGLE_PAY_MERCHANT_ID'),
    processOutKey: env('NEXT_PUBLIC_PO_KEY'),
    kaspiClientId: env('NEXT_PUBLIC_KASPI_CLIENT_ID'),
    kaspiPartnerId: env('NEXT_PUBLIC_KASPI_PARTNER_ID'),
    kaspiOauthUrl: env('NEXT_PUBLIC_KASPI_OAUTH_URL'),
    kaspiOpayUrl: env('NEXT_PUBLIC_KASPI_OPAY_URL'),
    incogniaSdkKey: env('NEXT_PUBLIC_INCOGNIA_SDK_KEY'),
    incogniaSdkUrl: env('NEXT_PUBLIC_INCOGNIA_SDK_URL'),
    isIncogniaEnabled: env('NEXT_PUBLIC_IS_INCOGNIA_ENABLED') === 'true',
    isDevtoolsEnabled: env('NEXT_PUBLIC_IS_DEVTOOLS_ENABLED') === 'true',
    helloSignClientId: env('NEXT_PUBLIC_HELLO_SIGN_CLIENT_ID'),
    shouldCheckCountryCode: env('NEXT_PUBLIC_SHOULD_CHECK_COUNTRY_CODE') === 'true',
    shouldCheckLanguageCode: env('NEXT_PUBLIC_SHOULD_CHECK_LANGUAGE_CODE') === 'true',
    useFallbackAppUrlValue: env('NEXT_PUBLIC_USE_FALLBACK_APP_URL_VALUE') === 'true',
  })

  if (!parseResult.success && !staticConfig.isBrowser) {
    // If aopConfig validation fails in server-side throw an error. This error will cause  a
    // 500 response to be sent to the client, which will render it in screen.

    const errorMessage = parseResult.error.message
    const errorIssues = parseResult.error.issues
    const message = `[AppConfigValidation]: ${errorMessage} \n\n${JSON.stringify(errorIssues, null, 2)}`
    throw new Error(message)
  }

  appConfig = {
    // we can safely use the non-null assertion here because any potential error is already thrown
    // in the server-side, and the only role of client after that error is to show it on the screen
    ...parseResult.data!,
    ...staticConfig,
  }

  return appConfig
}

export const injectAppVersion = () => {
  const { version, isBrowser } = getConfig()
  if (!isBrowser) {
    return
  }
  window.__appVersion = version
}
